/**
 * Consolidated types for grant employee access functionality
 * Provides type-safe interfaces for GrantEmployeeAccessModal and related components
 */

import { z } from "zod";

/**
 * Validation schema for grant employee access form
 */
export const grantEmployeeAccessSchema = z
  .object({
    userId: z.string().min(1, "Employee is required"),
    contactIds: z.array(z.string()),
    grantToAllClients: z.boolean().default(false),
  })
  .refine(
    (value) => {
      const { contactIds, grantToAllClients } = value;
      return grantToAllClients || (contactIds && contactIds.length > 0);
    },
    {
      message: "Either select specific clients or grant access to all clients",
      path: ["contactIds"],
    }
  );

/**
 * Form data type inferred from schema
 */
export type GrantEmployeeAccessFormData = z.infer<typeof grantEmployeeAccessSchema>;

/**
 * Client record for display
 */
export interface ClientRecord {
  id: string;
  name?: string;
  company_name?: string;
  email?: string;
}

/**
 * Client search result
 */
export interface ClientSearchResult extends ClientRecord {
  searchableText?: string;
}

/**
 * Employee access record
 */
export interface EmployeeAccessRecord {
  id: string;
  userId: string;
  contactId: string;
  user?: {
    id: string;
    email?: string;
    first_name?: string;
    last_name?: string;
  };
}

/**
 * Grant access mutation parameters
 */
export interface GrantAccessMutationParams {
  userId: string;
  contactId: string;
}

/**
 * Revoke access mutation parameters
 */
export interface RevokeAccessMutationParams {
  userId: string;
  contactId: string;
}

/**
 * Client search state
 */
export interface ClientSearchState {
  searchTerm: string;
  debouncedSearchTerm: string;
  isSearching: boolean;
  results: ClientRecord[];
}

/**
 * Grant access form state
 */
export interface GrantAccessFormState {
  isOpen: boolean;
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  success: boolean;
}

/**
 * Props for GrantAccessClientSelect component
 */
export interface GrantAccessClientSelectProps {
  value: string[];
  onValueChange: (values: string[]) => void;
  options: ClientSearchResult[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  isSearching: boolean;
  isLoading: boolean;
  disabled: boolean;
  grantToAllClients: boolean;
  isBulk?: boolean;
}

/**
 * Props for GrantAccessEmployeeSection component
 */
export interface GrantAccessEmployeeSectionProps {
  userId: string;
  onUserSelect: (userId: string) => void;
  error?: string;
  preSelectedUser?: {
    id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  };
}

/**
 * Props for GrantAccessExistingAccess component
 */
export interface GrantAccessExistingAccessProps {
  contactId: string;
  isLoading: boolean;
  onRevokeAccess: (userId: string) => void;
  isRevoking: boolean;
}

/**
 * Type guard to check if a value is a ClientRecord
 */
export function isClientRecord(value: unknown): value is ClientRecord {
  return (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    typeof (value as ClientRecord).id === "string"
  );
}

/**
 * Type guard to check if a value is an EmployeeAccessRecord
 */
export function isEmployeeAccessRecord(value: unknown): value is EmployeeAccessRecord {
  return (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    "userId" in value &&
    "contactId" in value &&
    typeof (value as EmployeeAccessRecord).id === "string" &&
    typeof (value as EmployeeAccessRecord).userId === "string" &&
    typeof (value as EmployeeAccessRecord).contactId === "string"
  );
}

/**
 * Type guard to check if an array contains EmployeeAccessRecords
 */
export function isEmployeeAccessRecordArray(value: unknown): value is EmployeeAccessRecord[] {
  return Array.isArray(value) && value.every(isEmployeeAccessRecord);
}

/**
 * Helper to format user display name
 */
export function formatUserName(user?: {
  first_name?: string;
  last_name?: string;
  email?: string;
}): string {
  if (!user) return "Unknown User";
  const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  return name || user.email || "Unknown User";
}

/**
 * Helper to format client display name
 */
export function formatClientName(client: ClientRecord): string {
  return client.company_name || client.name || `Client ${client.id}`;
}

/**
 * Helper to create searchable text from client
 */
export function createClientSearchableText(client: ClientRecord): string {
  return [client.name, client.company_name, client.email].filter(Boolean).join(" ");
}
